import Button from 'devextreme-react/button';
import FileUploader from 'devextreme-react/file-uploader';
import SelectBox from 'devextreme-react/select-box';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextArea from 'devextreme-react/text-area';
import { useShellLayout } from '@jarvis/platform-ui';
import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { useHasRole } from '../../../auth/useHasRole';
import { TimezoneBanner } from '../../../components/TimezoneBanner';
import type {
  BulkCreateTimeOffPayload,
  CalendarTarget,
  ImportResult,
  TimeOffStatus,
  TimeOffType
} from '../../../api/timeOff';
import { bulkCreateTimeOff, downloadCsvTemplateUrl, importTimeOffCsv, listCalendarTargets } from '../../../api/timeOff';
import { TimeOffCsvImportResults } from './TimeOffCsvImportResults';
import { CalendarTargetPicker } from './components/CalendarTargetPicker';
import { EmployeeUserPicker } from './components/EmployeeUserPicker';
import { TimeOffDurationFields, type TimeOffDurationDraft } from './components/TimeOffDurationFields';
import { defaultPartialDayRange, isTimeOffStartInPast } from './timeOffDateUtils';
import { confirmPastTimeOffCreate } from './timeOffPastStartWarning';
import { confirmCompanyHolidayTimeOffCreateMany } from './timeOffHolidayWarning';
import { confirmNoSameDayTimeOffConflict } from './timeOffSameDayWarning';
import { rowsAfterHolidayExclusion } from './timeOffHolidaySubmit';
import {
  importedTimeOffMessage,
  TimeOffSubmitResultPopup,
  type TimeOffSubmitResult
} from './TimeOffSubmitResultPopup';
import { useCompanyHolidays } from './useCompanyHolidays';

const TYPE_OPTIONS: TimeOffType[] = ['Vacation', 'Sick', 'Personal', 'Other'];
const STATUS_OPTIONS: TimeOffStatus[] = ['Pending', 'Approved', 'Rejected', 'Cancelled'];
const MAX_ROWS = 50;
const CALENDAR_QUESTION_LABEL = 'Which calendar should this show on?';

const calendarHintStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--ops-color-muted, #6b7280)',
  marginTop: 4,
  lineHeight: 1.35
};

interface ManualRow {
  rowKey: string;
  type: TimeOffType;
  status: TimeOffStatus;
  notes: string;
  isAllDay: boolean;
  startUtc: Date;
  endUtc: Date;
}

interface BulkManualForm {
  employeeUpn: string;
  employeeDisplayName: string;
  rows: ManualRow[];
  calendarTargetIds: string[];
}

function newRowKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultRow(seed?: Date): ManualRow {
  const { start, end } = defaultPartialDayRange(seed);
  return {
    rowKey: newRowKey(),
    type: 'Personal',
    status: 'Approved',
    notes: '',
    isAllDay: false,
    startUtc: start,
    endUtc: end
  };
}

function defaultManualForm(): BulkManualForm {
  return {
    employeeUpn: '',
    employeeDisplayName: '',
    rows: [defaultRow()],
    calendarTargetIds: []
  };
}


export function TimeOffManagementPage() {
  const { isNarrow } = useShellLayout();
  const navigate = useNavigate();
  const { isItSysAdmin } = useHasRole();
  const itSysAdmin = isItSysAdmin();
  const { holidays, disabledHolidayDates } = useCompanyHolidays();
  const [manualForm, setManualForm] = useState<BulkManualForm>(defaultManualForm);
  const [manualFormKey, setManualFormKey] = useState(0);
  const [submittingManual, setSubmittingManual] = useState(false);
  // Per-day errors keyed by 1-based day number (matches server RowNumber).
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [submitResult, setSubmitResult] = useState<TimeOffSubmitResult | null>(null);
  const [hrCalendarTargets, setHrCalendarTargets] = useState<CalendarTarget[]>([]);

  const [csvFiles, setCsvFiles] = useState<File[]>([]);
  const [csvUploaderKey, setCsvUploaderKey] = useState(0);
  const [csvTargetIds, setCsvTargetIds] = useState<string[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void listCalendarTargets()
      .then((rows) => {
        if (cancelled) return;
        const enabled = rows.filter((t) => t.isEnabled);
        setHrCalendarTargets(enabled);
        if (!itSysAdmin && enabled.length > 0) {
          const defaultId = enabled[0].id;
          setManualForm((prev) =>
            prev.calendarTargetIds.length === 0 ? { ...prev, calendarTargetIds: [defaultId] } : prev
          );
          setCsvTargetIds((prev) => (prev.length === 0 ? [defaultId] : prev));
        }
      })
      .catch(() => {
        if (!cancelled) setHrCalendarTargets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [itSysAdmin]);

  function updateRow(index: number, patch: Partial<ManualRow>) {
    setManualForm((prev) => {
      const rows = prev.rows.slice();
      rows[index] = { ...rows[index], ...patch };
      return { ...prev, rows };
    });
  }

  // Apply a duration-draft update against the live row state (functional
  // update) so chained/re-entrant changes always read the current values
  // rather than a stale render snapshot (ITB-1633).
  function updateRowDuration(index: number, updater: (prev: TimeOffDurationDraft) => TimeOffDurationDraft) {
    setManualForm((prev) => {
      const rows = prev.rows.slice();
      const current = rows[index];
      const next = updater({
        startLocal: current.startUtc,
        endLocal: current.endUtc,
        isAllDay: current.isAllDay
      });
      rows[index] = {
        ...current,
        startUtc: next.startLocal,
        endUtc: next.endLocal,
        isAllDay: next.isAllDay
      };
      return { ...prev, rows };
    });
  }

  function addRow() {
    setManualForm((prev) => {
      if (prev.rows.length >= MAX_ROWS) return prev;
      // Seed the new row from the previous row's start, bumped by 1 day, for convenience.
      const last = prev.rows[prev.rows.length - 1];
      const seed = new Date(last.startUtc);
      seed.setDate(seed.getDate() + 1);
      return { ...prev, rows: [...prev.rows, defaultRow(seed)] };
    });
  }

  function removeRow(index: number) {
    setManualForm((prev) => {
      if (prev.rows.length <= 1) return prev;
      const rows = prev.rows.slice();
      rows.splice(index, 1);
      return { ...prev, rows };
    });
    setRowErrors((prev) => {
      // Shift errors for rows after the removed one and drop the removed row's error.
      const next: Record<number, string> = {};
      const removedRowNumber = index + 1;
      for (const [k, v] of Object.entries(prev)) {
        const n = Number(k);
        if (n === removedRowNumber) continue;
        next[n > removedRowNumber ? n - 1 : n] = v;
      }
      return next;
    });
  }

  function resetManual() {
    const next = defaultManualForm();
    if (!itSysAdmin && hrCalendarTargets.length > 0) {
      next.calendarTargetIds = [hrCalendarTargets[0].id];
    }
    setManualForm(next);
    setManualFormKey((key) => key + 1);
    setRowErrors({});
  }

  function closeSubmitResult() {
    setSubmitResult(null);
  }

  function goToCalendarFromResult() {
    setSubmitResult(null);
    navigate('/hr/time-off/calendar');
  }

  async function submitManual() {
    if (!manualForm.employeeUpn.trim()) {
      setSubmitResult({ kind: 'failed', message: 'Employee is required.' });
      return;
    }
    if (manualForm.rows.length === 0) {
      setSubmitResult({ kind: 'failed', message: 'Add at least one time-off day.' });
      return;
    }
    for (let i = 0; i < manualForm.rows.length; i++) {
      const row = manualForm.rows[i];
      if (!row.isAllDay && row.endUtc < row.startUtc) {
        setRowErrors({ [i + 1]: 'End must not be before Start.' });
        setSubmitResult({ kind: 'failed', message: `Day ${i + 1}: End must not be before Start.` });
        return;
      }
    }

    const pastRow = manualForm.rows.find((row) => isTimeOffStartInPast(row.startUtc, row.isAllDay));
    if (pastRow) {
      const proceed = await confirmPastTimeOffCreate(pastRow.startUtc, pastRow.isAllDay);
      if (!proceed) return;
    }

    const holidayDrafts = manualForm.rows.map((row) => ({
      startLocal: row.startUtc,
      endLocal: row.endUtc,
      isAllDay: row.isAllDay
    }));
    const proceedHoliday = await confirmCompanyHolidayTimeOffCreateMany(holidayDrafts, holidays);
    if (!proceedHoliday) return;

    const excludedRows = manualForm.rows.flatMap((row) =>
      rowsAfterHolidayExclusion(row.startUtc, row.endUtc, row.isAllDay, holidays, {
        type: row.type,
        status: row.status,
        notes: row.notes.trim() || null
      })
    );
    if (excludedRows.length === 0) {
      setSubmitResult({ kind: 'failed', message: 'All selected days are company holidays.' });
      return;
    }

    const proceedSameDay = await confirmNoSameDayTimeOffConflict({
      employeeUpn: manualForm.employeeUpn.trim(),
      drafts: excludedRows
    });
    if (!proceedSameDay) return;

    setSubmittingManual(true);
    setRowErrors({});
    try {
      const payload: BulkCreateTimeOffPayload = {
        employeeUpn: manualForm.employeeUpn.trim(),
        employeeDisplayName: manualForm.employeeDisplayName.trim() || null,
        calendarTargetIds: manualForm.calendarTargetIds,
        rows: excludedRows
      };
      const result = await bulkCreateTimeOff(payload);
      if (result.errors.length > 0) {
        const map: Record<number, string> = {};
        const generalParts: string[] = [];
        for (const err of result.errors) {
          if (err.rowNumber > 0) {
            const existing = map[err.rowNumber];
            map[err.rowNumber] = existing ? `${existing} ${err.message}` : err.message;
          } else {
            generalParts.push(`${err.field}: ${err.message}`);
          }
        }
        setRowErrors(map);
        const failedRowCount = Object.keys(map).length;
        const message =
          generalParts.length > 0 && failedRowCount === 0
            ? generalParts.join(' ')
            : generalParts.length > 0
              ? `${generalParts.join(' ')} (${failedRowCount} row error(s) below)`
              : `No entries created. ${failedRowCount} row error(s) below.`;
        setSubmitResult({ kind: 'failed', message });
        return;
      }
      resetManual();
      setSubmitResult({ kind: 'success', count: result.inserted });
    } catch (err) {
      setSubmitResult({
        kind: 'failed',
        message: err instanceof Error ? err.message : 'Failed to create time off entries.'
      });
    } finally {
      setSubmittingManual(false);
    }
  }

  async function submitCsvImport() {
    if (csvFiles.length === 0) {
      setSubmitResult({ kind: 'failed', message: 'Choose a CSV file to import.' });
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importTimeOffCsv(csvFiles[0], csvTargetIds);
      setImportResult(result);
      if (result.inserted > 0) {
        setCsvFiles([]);
        setCsvUploaderKey((key) => key + 1);
        setSubmitResult({
          kind: 'success',
          count: result.inserted,
          message: importedTimeOffMessage(result.inserted, result.errors.length),
          againText: 'Import another'
        });
        return;
      }
      if (result.errors.length > 0) {
        setSubmitResult({
          kind: 'failed',
          message: `No rows imported. ${result.errors.length} error(s) reported below.`
        });
        return;
      }
      setSubmitResult({ kind: 'failed', message: 'No rows imported.' });
    } catch (err) {
      setSubmitResult({
        kind: 'failed',
        message: err instanceof Error ? err.message : 'CSV import failed.'
      });
    } finally {
      setImporting(false);
    }
  }

  const rowCount = manualForm.rows.length;
  const hrCalendarHint =
    !itSysAdmin && hrCalendarTargets.length > 0
      ? `Posting to ${hrCalendarTargets[0].displayName} (${hrCalendarTargets[0].segment === 'Contract' ? 'Contract' : 'Company'})`
      : null;

  function renderCalendarSection(
    value: string[],
    onChange: (ids: string[]) => void,
    defaultResetKey?: number | string
  ) {
    if (itSysAdmin) {
      return (
        <>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{CALENDAR_QUESTION_LABEL}</label>
          <CalendarTargetPicker
            value={value}
            onChange={onChange}
            defaultToFirst
            defaultSegment="Company"
            defaultResetKey={defaultResetKey}
          />
        </>
      );
    }

    if (hrCalendarHint) {
      return <div style={calendarHintStyle}>{hrCalendarHint}</div>;
    }

    return (
      <div style={{ ...calendarHintStyle, color: 'var(--ops-color-danger, #b91c1c)' }}>
        No enabled calendar is available for your role. Ask an administrator to register calendar targets.
      </div>
    );
  }

  return (
    <div className="ops-page">
      <section className="ops-section">
        <div className="ops-section__header">
          <h3 className="ops-section__title">Add Time Off</h3>
          {!isNarrow ? <TimezoneBanner /> : null}
        </div>
          <TabPanel deferRendering={false} animationEnabled showNavButtons swipeEnabled={false}>
            <TabItem title="Manual entry">
              <div style={{ paddingTop: '0.75rem' }}>
                <EmployeeUserPicker
                  key={manualFormKey}
                  upn={manualForm.employeeUpn}
                  displayName={manualForm.employeeDisplayName}
                  onChange={(employee) =>
                    setManualForm((prev) => ({
                      ...prev,
                      employeeUpn: employee.upn,
                      employeeDisplayName: employee.displayName
                    }))
                  }
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {manualForm.rows.map((row, index) => (
                    <ManualRowCard
                      key={row.rowKey}
                      index={index}
                      total={manualForm.rows.length}
                      row={row}
                      error={rowErrors[index + 1] ?? null}
                      onDurationChange={(updater) => updateRowDuration(index, updater)}
                      onTypeChange={(value) => updateRow(index, { type: value })}
                      onStatusChange={(value) => updateRow(index, { status: value })}
                      onNotesChange={(value) => updateRow(index, { notes: value })}
                      onRemove={() => removeRow(index)}
                      disabled={submittingManual}
                      disabledHolidayDates={disabledHolidayDates}
                    />
                  ))}
                </div>

                <div className="time-off-management-add-row" style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    text="Add another date"
                    icon="add"
                    stylingMode="outlined"
                    onClick={addRow}
                    disabled={submittingManual || rowCount >= MAX_ROWS}
                  />
                  <span style={{ color: 'var(--ops-color-muted, #6b7280)', fontSize: 12 }}>
                    {rowCount} of {MAX_ROWS} days
                  </span>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  {renderCalendarSection(
                    manualForm.calendarTargetIds,
                    (ids) => setManualForm((prev) => ({ ...prev, calendarTargetIds: ids })),
                    manualFormKey
                  )}
                </div>
                <div className="time-off-management-actions" style={{ marginTop: '0.75rem' }}>
                  <Button
                    text={submittingManual ? 'Submitting…' : 'Submit'}
                    icon="save"
                    type="default"
                    stylingMode="contained"
                    onClick={() => void submitManual()}
                    disabled={submittingManual}
                  />
                  <Button
                    text="Cancel"
                    icon="close"
                    onClick={resetManual}
                    disabled={submittingManual}
                  />
                </div>
              </div>
            </TabItem>

            <TabItem title="Bulk CSV">
              <div className="time-off-management-bulk-csv" style={{ paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div className="time-off-management-bulk-csv__header" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                  <a href={downloadCsvTemplateUrl()} download="time-off-import-template.csv">
                    Download CSV template
                  </a>
                  <span style={{ color: 'var(--ops-color-muted, #6b7280)' }}>
                    Header: <code>employeeUpn,type,start,end,isAllDay,notes,status</code>
                  </span>
                </div>
                <FileUploader
                  key={csvUploaderKey}
                  uploadMode="useForm"
                  multiple={false}
                  accept=".csv,text/csv"
                  selectButtonText="Choose CSV"
                  labelText="or drop CSV file here"
                  onValueChanged={(e: { value?: File[] }) => setCsvFiles(e.value ?? [])}
                />
                <div>
                  {renderCalendarSection(csvTargetIds, setCsvTargetIds)}
                </div>
                <div className="time-off-management-actions">
                  <Button
                    text={importing ? 'Importing…' : 'Import CSV'}
                    icon="upload"
                    type="default"
                    stylingMode="contained"
                    onClick={() => void submitCsvImport()}
                    disabled={importing || csvFiles.length === 0}
                  />
                </div>
                {importResult ? <TimeOffCsvImportResults result={importResult} /> : null}
              </div>
            </TabItem>
          </TabPanel>
        </section>
      <TimeOffSubmitResultPopup
        result={submitResult}
        onClose={closeSubmitResult}
        onGoToCalendar={goToCalendarFromResult}
      />
    </div>
  );
}

interface ManualRowCardProps {
  index: number;
  total: number;
  row: ManualRow;
  error: string | null;
  disabled?: boolean;
  disabledHolidayDates?: Date[];
  onDurationChange: (updater: (prev: TimeOffDurationDraft) => TimeOffDurationDraft) => void;
  onTypeChange: (value: TimeOffType) => void;
  onStatusChange: (value: TimeOffStatus) => void;
  onNotesChange: (value: string) => void;
  onRemove: () => void;
}

function ManualRowCard({
  index,
  total,
  row,
  error,
  disabled,
  onDurationChange,
  onTypeChange,
  onStatusChange,
  onNotesChange,
  onRemove,
  disabledHolidayDates
}: ManualRowCardProps) {
  const durationDraft = {
    startLocal: row.startUtc,
    endLocal: row.endUtc,
    isAllDay: row.isAllDay
  };

  return (
    <div className="time-off-row-card">
      <div className="time-off-row-card__header">
        {total > 1 ? <strong className="time-off-row-card__row-label">Day {index + 1}</strong> : <span />}
        <Button
          text="Remove"
          icon="trash"
          stylingMode="text"
          onClick={onRemove}
          disabled={disabled || total <= 1}
        />
      </div>

      <TimeOffDurationFields
        draft={durationDraft}
        onDraftChange={onDurationChange}
        disabled={disabled}
        disabledDates={disabledHolidayDates}
      />

      <div className="time-off-row-card__fields-grid" style={{ marginTop: '0.75rem' }}>
        <div>
          <label className="time-off-row-card__field-label">Type</label>
          <SelectBox
            items={TYPE_OPTIONS}
            value={row.type}
            onValueChanged={(e) => {
              if (e.value) onTypeChange(e.value as TimeOffType);
            }}
            disabled={disabled}
            width="100%"
          />
        </div>
        <div>
          <label className="time-off-row-card__field-label">Status</label>
          <SelectBox
            items={STATUS_OPTIONS}
            value={row.status}
            onValueChanged={(e) => {
              if (e.value) onStatusChange(e.value as TimeOffStatus);
            }}
            disabled={disabled}
            width="100%"
          />
        </div>
      </div>

      <div style={{ marginTop: '0.5rem' }}>
        <label className="time-off-row-card__field-label">Notes</label>
        <TextArea
          value={row.notes}
          onValueChanged={(e) => onNotesChange(String(e.value ?? ''))}
          placeholder="Optional notes"
          height={60}
          disabled={disabled}
        />
      </div>

      {error ? (
        <div role="alert" className="time-off-row-card__error">
          {error}
        </div>
      ) : null}
    </div>
  );
}
